import { Feature, FeatureCollection, GeoJsonObject, Geometry, MultiPolygon, Polygon, Position } from "geojson";
import { MapPoint } from "../types/scheduling/territory";
import polylabel from "./polylabel";
import { LngLat, LngLatBounds, LngLatBoundsLike, LngLatLike } from "maplibre-gl";
import { LabeledLayer } from "../components/congregation/territory/common";

export const DefaultSrid = 4326;

// Global cache for polylabel results, keyed by a string identifier (typically territory ID).
// Each entry stores the computed center and the boundaries string used to compute it,
// so we can detect staleness without recomputing.
const polylabelCache = new Map<string, { center: Position; boundariesKey: string }>();

/**
 * Returns a cached polygon center if the boundaries haven't changed,
 * otherwise computes, caches, and returns a fresh one.
 * @param polygon The polygon coordinates
 * @param id A stable identifier for caching (e.g. territory ID)
 * @param boundariesKey The raw boundaries string for staleness detection
 */
export function cachedPolygonCenter(polygon: Polygon, id: string, boundariesKey: string): Position {
  const entry = polylabelCache.get(id);
  if (entry && entry.boundariesKey === boundariesKey) return entry.center;

  const center = polylabel(polygon.coordinates, 0.000001, false, id).position;
  polylabelCache.set(id, { center, boundariesKey });
  return center;
}

/* geoJsonMultiPolygon takes geojson and sees if it has 1 or more Polygon features.
   If it does, it modifies them to all be part of a MultiPolygon.
   This is intended for use with KML import, where we get a FeatureCollection.
 */
export function geoJsonMultiPolygon(geoJson: FeatureCollection): Feature {
  const multiPolygons: Position[][][] = [];
  geoJson.features.forEach((gf) => {
    if (gf.geometry.type === "Polygon") multiPolygons.push(gf.geometry.coordinates);
  });

  return {
    type: "Feature",
    geometry: {
      type: "MultiPolygon",
      coordinates: multiPolygons,
    },
    properties: {},
  };
}

export function polygonToMultiPolygon(polygon: Polygon): MultiPolygon {
  return {
    type: "MultiPolygon",
    coordinates: [polygon.coordinates],
  };
}

export function multipolygonToPolygons(multiPolygon: MultiPolygon): Polygon[] {
  return multiPolygon.coordinates.map((mp): Polygon => {
    return {
      type: "Polygon",
      coordinates: mp,
    };
  });
}

export function getPolygon(feature: Polygon | MultiPolygon): Polygon {
  if (feature.type === "MultiPolygon" && feature.coordinates.length) {
    return multipolygonToPolygons(feature)[0]!;
  }
  return feature as Polygon;
}

export function polygonCenter(polygon: Polygon, identifier?: string): Position {
  return polylabel(polygon.coordinates, 0.000001, false, identifier).position;
}

export function mapPointFromPosition(p: Position): MapPoint {
  return {
    x: p[0] ?? 0,
    y: p[1] ?? 0,
    srid: DefaultSrid,
  };
}

export function validMapPoint(mp?: MapPoint): boolean {
  if (!mp) return false;
  if (mp.x === 0 && mp.y === 0) return false;
  return mp.x >= -180 && mp.x <= 180 && mp.y >= -90 && mp.y <= 90;
}

export function positionFromMapPoint(mp: MapPoint): Position {
  return [mp.x, mp.y];
}

export function isValidLatLng(lat: number, lng: number): boolean {
  const validLat = Number.isFinite(lat) && Math.abs(lat) <= 90;
  const validLng = Number.isFinite(lng) && Math.abs(lng) <= 180;
  return validLat && validLng;
}

export function latLngToMapPoint(lat: number, lng: number): MapPoint {
  if (typeof lat !== "number") lat = Number(lat);
  if (typeof lng !== "number") lng = Number(lng);

  return {
    x: lng,
    y: lat,
    srid: DefaultSrid,
  };
}

/**
 * Converts a GeoJSON Position to a LngLat object
 * @param position GeoJSON Position [longitude, latitude(, elevation)]
 * @returns LngLat object that can be used with MapLibre methods
 */
function positionToLngLat(position: Position): LngLat {
  // GeoJSON Position is [longitude, latitude(, elevation)]
  // We only need longitude and latitude for bounds
  return new LngLat(position[0] ?? 0, position[1] ?? 1);
}

/**
 * Extends a MapLibre LngLatBounds object with the coordinates from a GeoJSON geometry
 * @param bounds LngLatBounds object to extend
 * @param geometry GeoJSON geometry object
 */
export function extendBoundsWithGeometry(bounds: LngLatBounds, geometry: Geometry): void {
  // Helper function to safely extend bounds only with valid coordinates
  const safeExtend = (position: Position): void => {
    const lng = position[0];
    const lat = position[1];
    if (lng !== undefined && lat !== undefined && isValidLatLng(lat, lng)) {
      bounds.extend(positionToLngLat(position));
    }
  };

  switch (geometry.type) {
    case "Point":
      safeExtend(geometry.coordinates as Position);
      break;

    case "LineString":
    case "MultiPoint":
      geometry.coordinates.forEach((coord: Position) => safeExtend(coord));
      break;

    case "Polygon":
    case "MultiLineString":
      geometry.coordinates.forEach((ring: Position[]) => ring.forEach((coord: Position) => safeExtend(coord)));
      break;

    case "MultiPolygon":
      geometry.coordinates.forEach((polygon: Position[][]) =>
        polygon.forEach((ring: Position[]) => ring.forEach((coord: Position) => safeExtend(coord))),
      );
      break;

    case "GeometryCollection":
      geometry.geometries.forEach((geom: Geometry) => extendBoundsWithGeometry(bounds, geom));
      break;
  }
}

export function labeledLayersToFeatureCollection(labeledLayers: LabeledLayer[]): FeatureCollection {
  const getCenter = (ll: LabeledLayer): Position | undefined => {
    if (ll.center) return ll.center;
    const f = ll.geojson;
    if (f.type === "MultiPolygon") {
      const polygons = multipolygonToPolygons(f);
      if (polygons.length && polygons[0]) {
        return polygonCenter(polygons[0]);
      }
    } else if (f.type === "Polygon") {
      return polygonCenter(f);
    }
  };

  return {
    type: "FeatureCollection",
    features: labeledLayers.flatMap((ll): Feature[] => {
      const center = getCenter(ll);
      const features: Feature[] = [
        {
          type: "Feature",
          geometry: ll.geojson,
          properties: {
            disableEdit: true,
          },
        },
      ];
      if (center) {
        features.push({
          type: "Feature",
          geometry: {
            type: "Point",
            coordinates: center,
          },
          properties: {
            id: ll.id,
            label: ll.label,
            disableEdit: true,
          },
        });
      }
      return features;
    }),
  };
}

export function getMapBounds(
  geojson?: GeoJsonObject,
  annotations?: FeatureCollection,
  addresses?: FeatureCollection,
  congTerritory?: Polygon,
  congLocation?: MapPoint,
): LngLatBoundsLike | undefined {
  const geoObjects: FeatureCollection[] = [];

  if (geojson) {
    if (geojson.type === "FeatureCollection") geoObjects.push(geojson as FeatureCollection);
    else if (geojson.type === "Polygon" || geojson.type === "MultiPolygon")
      geoObjects.push({
        type: "FeatureCollection",
        features: [{ type: "Feature", properties: null, geometry: geojson as Geometry }],
      });
  }
  if (annotations && annotations.features.length) geoObjects.push(annotations);
  if (addresses && addresses.features.length) geoObjects.push(addresses);
  const geoBounds = new LngLatBounds();
  geoObjects.forEach((geoObject) => {
    if (geoObject.features?.length) {
      geoObject.features.forEach((feature) => {
        extendBoundsWithGeometry(geoBounds, feature.geometry);
      });
    }
  });
  if (geoBounds && !geoBounds.isEmpty()) return geoBounds;

  if (congTerritory) {
    // center it in the congregation's territory
    const geoBounds = new LngLatBounds();
    extendBoundsWithGeometry(geoBounds, congTerritory);
    if (geoBounds && !geoBounds.isEmpty()) return geoBounds;
  }

  if (congLocation && validMapPoint(congLocation)) {
    // center it on the congregation's meeting place
    const loc: LngLatLike = { lon: congLocation.x, lat: congLocation.y };
    const geoBounds = new LngLatBounds(loc, loc);
    if (!geoBounds.isEmpty()) return geoBounds;
  }

  // we don't know where to center it. the map will be the entire world
  return;
}

export function findLabeledLayers(bounds: LngLatBounds, allLabeledLayers: LabeledLayer[]): LabeledLayer[] {
  // find which of all the labeled layers are on the map
  try {
    const center = !bounds.isEmpty() ? bounds.getCenter() : undefined;
    if (!center) return allLabeledLayers.slice(0, 30);

    // Use pre-computed centers from LabeledLayer when available, falling back to polygonCenter
    const distanceMap = new Map<number, number>();
    for (const ll of allLabeledLayers) {
      try {
        const llCenterPos = ll.center ?? polygonCenter(getPolygon(ll.geojson), ll.id.toString());
        const llCenter = positionToLngLat(llCenterPos);
        distanceMap.set(ll.id, center.distanceTo(llCenter));
      } catch (err: any) {
        console.error("computing labeled layer center", err);
        distanceMap.set(ll.id, Infinity);
      }
    }

    const layers = [...allLabeledLayers].sort((lla, llb) => {
      return (distanceMap.get(lla.id) ?? Infinity) - (distanceMap.get(llb.id) ?? Infinity);
    });
    // never more than 30 for performance reasons
    return layers.slice(0, 30);
  } catch (err: any) {
    console.error("error finding labeled layers", err);
    return [];
  }
}

export interface Coordinates {
  lat: number;
  lng: number;
}

export async function getCurrentLocation(): Promise<Coordinates> {
  return new Promise((resolve, reject) => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position: GeolocationPosition) => {
          const coordinates: Coordinates = {
            lat: position.coords.latitude,
            lng: position.coords.longitude,
          };
          resolve(coordinates);
        },
        (error: GeolocationPositionError) => {
          reject(error);
        },
      );
    } else {
      reject(new Error("Geolocation is not supported by this browser."));
    }
  });
}
